// Pure helpers for the Azure direct upload flow (no React / Redux / HTTP here).
import { AzureFileEntityTypeEnum, AzureFileTypeEnum, StorageOwnerTypeEnum } from '../models/Enums/AzureFileTypeEnum';
import {
  AllowedAzureFileType,
  AzureFileLinkKind,
  AzureFileListLoaderKind,
  AzureFileOriginEnum,
  OwnerFileViewModel,
  AzureFileTarget,
  ProviderFileViewModel,
  RegisteredAzureFile,
  SettlementFileViewModel,
  ServiceFileViewModel,
  SponsorFileViewModel,
  PaymentReceiptViewModel,
  CreditorInvoiceFileViewModel,
  AzureFileOwner,
  DrawerStorageFile,
  TargetStorageFile,
  TargetStorageFolder,
  ProviderFileTypeEnum,
} from '../models/Entities/AzureFiles/AzureFileUpload';
import { getOwnerFolderPath, getParentPath, getPathName } from './StorageBrowser.Functions';

/** Allowed extensions per file type (integration contract §3). Lower case, no dot. */
export const AZURE_ALLOWED_EXTENSIONS: Record<AzureFileTypeEnum, string[]> = {
  [AzureFileTypeEnum.SponsorPaymentProposal]: ['pdf', 'xlsx', 'xls', 'csv', 'doc', 'docx'],
  [AzureFileTypeEnum.ProtocolContract]: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'xml', 'zip'],
  [AzureFileTypeEnum.ProviderInvoice]: ['pdf'],
  [AzureFileTypeEnum.ProviderServicesPerformed]: ['xlsx', 'xls', 'csv', 'pdf', 'docx'],
  [AzureFileTypeEnum.SettlementInvoice]: ['pdf'],
  [AzureFileTypeEnum.ServiceReceipt]: ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic'],
  [AzureFileTypeEnum.PractitionerInvoice]: ['pdf', 'xml', 'jpg', 'jpeg', 'png', 'webp', 'heic'],
  [AzureFileTypeEnum.PaymentReceipt]: ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic'],
};

/** Maximum accepted size for a single file (50 MB, same limit Contracts applies to its files). */
export const AZURE_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

/** Delay before reloading the registered files list after an upload (the owner service registers asynchronously). */
export const AZURE_REGISTRATION_RELOAD_DELAY_MS = 3000;

const buildAllowedType = (fileType: AzureFileTypeEnum, labelKey: string): AllowedAzureFileType => ({
  fileType,
  labelKey,
  extensions: AZURE_ALLOWED_EXTENSIONS[fileType],
});

/** Provider consumptions list: invoice (pdf) + services performed (spreadsheets). */
export const PROVIDER_ALLOWED_FILE_TYPES: AllowedAzureFileType[] = [
  buildAllowedType(AzureFileTypeEnum.ProviderInvoice, 'azureFileUpload.fileTypes.providerInvoice'),
  buildAllowedType(AzureFileTypeEnum.ProviderServicesPerformed, 'azureFileUpload.fileTypes.providerServicesPerformed'),
];

/** Protocol (sponsor) consumptions list: payment proposal. */
export const PROTOCOL_SPONSOR_ALLOWED_FILE_TYPES: AllowedAzureFileType[] = [
  buildAllowedType(AzureFileTypeEnum.SponsorPaymentProposal, 'azureFileUpload.fileTypes.sponsorPaymentProposal'),
];

/** Settlement detail: one or many invoices. */
export const SETTLEMENT_ALLOWED_FILE_TYPES: AllowedAzureFileType[] = [
  buildAllowedType(AzureFileTypeEnum.SettlementInvoice, 'azureFileUpload.fileTypes.settlementInvoice'),
];

/** Service (patient cash consumption): receipts (pdf / photos), contract §10. */
export const SERVICE_ALLOWED_FILE_TYPES: AllowedAzureFileType[] = [
  buildAllowedType(AzureFileTypeEnum.ServiceReceipt, 'azureFileUpload.fileTypes.serviceReceipt'),
];

/** Practitioner account statement: invoices of the practitioner (contract §12.1). */
export const PRACTITIONER_INVOICE_ALLOWED_FILE_TYPES: AllowedAzureFileType[] = [
  buildAllowedType(AzureFileTypeEnum.PractitionerInvoice, 'azureFileUpload.fileTypes.practitionerInvoice'),
];

/** Payments: receipts of the payment (transfer, receipt), contract §12.1. */
export const PAYMENT_RECEIPT_ALLOWED_FILE_TYPES: AllowedAzureFileType[] = [
  buildAllowedType(AzureFileTypeEnum.PaymentReceipt, 'azureFileUpload.fileTypes.paymentReceipt'),
];

/** Extension in lower case without dot; empty string when the name has none. */
export const getExtension = (fileName: string): string => {
  const name = (fileName ?? '').trim();
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return '';
  return name.substring(dot + 1).toLowerCase();
};

/** File name without its extension. */
export const getBaseName = (fileName: string): string => {
  const name = (fileName ?? '').trim();
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return name;
  return name.substring(0, dot);
};

const pad = (value: number): string => value.toString().padStart(2, '0');

/** yyyyMMddTHHmmss (UTC). */
export const formatBlobTimestamp = (date: Date): string =>
  `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
  `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}`;

/**
 * Name sent to Contracts (contract §7): original name without extension + "_" + timestamp.
 * Path separators are replaced so the name never adds segments to the blob path.
 */
export const buildBlobFileName = (originalName: string, date: Date = new Date()): string => {
  const base = getBaseName(originalName).replace(/[\\/]+/g, '_').trim() || 'archivo';
  return `${base}_${formatBlobTimestamp(date)}`;
};

/** Validation error as an i18n key, or undefined when the file is valid for the type. */
export const validateFile = (
  file: Pick<File, 'name' | 'size'>,
  fileType: AzureFileTypeEnum | undefined,
  allowedTypes: AllowedAzureFileType[],
  maxSizeBytes: number = AZURE_MAX_FILE_SIZE_BYTES
): string | undefined => {
  const extension = getExtension(file.name);
  if (!extension) return 'azureFileUpload.validation.noExtension';
  if (file.size === 0) return 'azureFileUpload.validation.emptyFile';
  if (file.size > maxSizeBytes) return 'azureFileUpload.validation.tooLarge';
  if (fileType === undefined) {
    return suggestFileType(file.name, allowedTypes) === undefined
      ? 'azureFileUpload.validation.invalidExtension'
      : 'azureFileUpload.validation.selectType';
  }
  const allowed = allowedTypes.find((type) => type.fileType === fileType);
  if (!allowed) return 'azureFileUpload.validation.typeNotAllowed';
  if (!allowed.extensions.includes(extension)) return 'azureFileUpload.validation.extensionNotAllowedForType';
  return undefined;
};

/** First allowed type that accepts the file extension (pdf → invoice, xlsx/xls/csv → services...). */
export const suggestFileType = (
  fileName: string,
  allowedTypes: AllowedAzureFileType[]
): AzureFileTypeEnum | undefined => {
  const extension = getExtension(fileName);
  if (!extension) return undefined;
  return allowedTypes.find((type) => type.extensions.includes(extension))?.fileType;
};

/** Value for the input[type=file] accept attribute. */
export const buildAcceptAttribute = (allowedTypes: AllowedAzureFileType[]): string =>
  Array.from(new Set(allowedTypes.flatMap((type) => type.extensions)))
    .map((extension) => `.${extension}`)
    .join(',');

/** Unique extensions of the allowed types, for display. */
export const listAllowedExtensions = (allowedTypes: AllowedAzureFileType[]): string[] =>
  Array.from(new Set(allowedTypes.flatMap((type) => type.extensions)));

/** Mime type for the PUT to Azure. */
export const getUploadContentType = (file: Pick<File, 'type'>): string => file.type || 'application/octet-stream';

/** Only drags carrying files are relevant (rows, text or links are ignored). */
export const dataTransferHasFiles = (dataTransfer: DataTransfer | null | undefined): boolean => {
  if (!dataTransfer?.types) return false;
  return Array.from(dataTransfer.types as ArrayLike<string>).includes('Files');
};

/** Human readable size (B, KB, MB, GB). */
export const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
};

/** Id of the target in the owner service routes and in the blob path: the GUID key when present, else the int id. */
export const getTargetRouteId = (target: Pick<AzureFileTarget, 'entityId' | 'entityKey'>): number | string =>
  target.entityKey ? target.entityKey : target.entityId;

/** Key used to store registered files per target in the store (`Service:{guid}`, `Provider:45`...). */
export const getAzureTargetKey = (target: AzureFileTarget): string =>
  `${target.entityType}:${target.entityKey ? target.entityKey.toLowerCase() : target.entityId}`;

const fullName = (fileName: string, fileExtension: string): string =>
  fileExtension && !fileName.toLowerCase().endsWith(`.${fileExtension.toLowerCase()}`)
    ? `${fileName}.${fileExtension}`
    : fileName;

/** Missing / unknown origin → Uploaded. */
export const toOrigin = (origin: number | null | undefined): AzureFileOriginEnum =>
  origin === AzureFileOriginEnum.Linked ? AzureFileOriginEnum.Linked : AzureFileOriginEnum.Uploaded;

export const mapProviderFile = (file: ProviderFileViewModel): RegisteredAzureFile => ({
  id: String(file.id),
  fileName: fullName(file.fileName, file.fileExtension),
  fileExtension: file.fileExtension,
  fileSize: file.fileSize,
  uploadedAt: file.uploadedAt,
  blobPath: file.blobPath,
  typeLabelKey:
    file.fileType === 2
      ? 'azureFileUpload.fileTypes.providerServicesPerformed'
      : 'azureFileUpload.fileTypes.providerInvoice',
  origin: toOrigin(file.origin),
});

export const mapSponsorFile = (file: SponsorFileViewModel): RegisteredAzureFile => ({
  id: String(file.id),
  fileName: fullName(file.fileName, file.fileExtension),
  fileExtension: file.fileExtension,
  fileSize: file.fileSize,
  uploadedAt: file.uploadedAt,
  blobPath: file.blobPath,
  typeLabelKey: 'azureFileUpload.fileTypes.sponsorPaymentProposal',
  origin: toOrigin(file.origin),
});

export const mapSettlementFile = (file: SettlementFileViewModel): RegisteredAzureFile => ({
  id: String(file.id),
  fileName: fullName(file.fileName, file.fileExtension),
  fileExtension: file.fileExtension,
  fileSize: file.fileSize,
  uploadedAt: file.uploadedAt,
  blobPath: file.blobPath,
  typeLabelKey: 'azureFileUpload.fileTypes.settlementInvoice',
  origin: toOrigin(file.origin),
});

export const mapServiceFile = (file: ServiceFileViewModel): RegisteredAzureFile => ({
  id: String(file.id),
  fileName: fullName(file.fileName, file.fileExtension),
  fileExtension: file.fileExtension,
  fileSize: file.fileSize,
  uploadedAt: file.uploadedAt,
  blobPath: file.blobPath,
  typeLabelKey: 'azureFileUpload.fileTypes.serviceReceipt',
  origin: toOrigin(file.origin),
});

/** Relative path of a CashFlow record: `path` when the API sends it, else the blob path without the tenant. */
const relativeOf = (file: { path?: string | null; blobPath?: string | null }): string =>
  file.path || stripTenantSegment(file.blobPath);

export const mapPaymentReceiptFile = (file: PaymentReceiptViewModel): RegisteredAzureFile => ({
  id: String(file.id),
  fileName: fullName(file.fileName, file.fileExtension),
  fileExtension: file.fileExtension,
  fileSize: file.fileSize,
  uploadedAt: file.uploadedAt || file.created || '',
  blobPath: file.blobPath,
  typeLabelKey: 'azureFileUpload.fileTypes.paymentReceipt',
  origin: toOrigin(file.origin),
  path: relativeOf(file),
});

export const mapCreditorInvoiceFile = (file: CreditorInvoiceFileViewModel): RegisteredAzureFile => ({
  id: String(file.id),
  fileName: fullName(file.fileName, file.fileExtension),
  fileExtension: file.fileExtension,
  fileSize: file.fileSize,
  uploadedAt: file.created || file.invoiceDate || '',
  blobPath: file.blobPath,
  typeLabelKey: 'azureFileUpload.fileTypes.practitionerInvoice',
  origin: AzureFileOriginEnum.Uploaded,
  path: relativeOf(file),
});

/** Payment receipts (contract §12.1): owner = practitioner GUID (creditor / receiver of the payment). */
export const buildPaymentReceiptTarget = (paymentId: number | null | undefined, practitionerId: string | null | undefined): AzureFileTarget | undefined =>
  isPositiveInt(paymentId) && isGuid(practitionerId)
    ? {
        entityType: AzureFileEntityTypeEnum.Payment,
        entityId: paymentId,
        owner: { ownerType: StorageOwnerTypeEnum.Practitioner, ownerId: practitionerId.toLowerCase() },
      }
    : undefined;

/** Practitioner invoices (contract §12.1): entityKey = owner = practitioner GUID, no protocol. */
export const buildPractitionerInvoiceTarget = (practitionerId: string | null | undefined): AzureFileTarget | undefined =>
  isGuid(practitionerId)
    ? {
        entityType: AzureFileEntityTypeEnum.Practitioner,
        entityId: 0,
        entityKey: practitionerId.toLowerCase(),
        owner: { ownerType: StorageOwnerTypeEnum.Practitioner, ownerId: practitionerId.toLowerCase() },
      }
    : undefined;

// ---- Storage owner per entry point (contract §2, "Request de SAS ampliado") ----

const isPositiveInt = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

/** Provider consumptions: owner Provider = Provider.id, no protocol. */
export const getProviderStorageOwner = (providerId: number | null | undefined): AzureFileOwner | undefined =>
  isPositiveInt(providerId)
    ? { ownerType: StorageOwnerTypeEnum.Provider, ownerId: String(providerId) }
    : undefined;

/** Protocol (sponsor) consumptions: owner Sponsor = sponsor of the protocol contract, protocolId = protocol. */
export const getProtocolSponsorStorageOwner = (
  protocolId: number | null | undefined,
  sponsorId: number | null | undefined
): AzureFileOwner | undefined =>
  isPositiveInt(protocolId) && isPositiveInt(sponsorId)
    ? { ownerType: StorageOwnerTypeEnum.Sponsor, ownerId: String(sponsorId), protocolId }
    : undefined;

/** GUID in "D" format (36 chars with hyphens), the only non-integer entity key Contracts accepts (contract §10.1). */
export const isGuid = (value: string | null | undefined): value is string =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value ?? '');

/** First sponsor of a contract (same rule as the protocol consumptions upload). */
export const getContractSponsorId = (
  contract: { sponsors?: { sponsorId?: number | null }[] | null } | null | undefined
): number | undefined => {
  const sponsorId = contract?.sponsors?.find((sponsor) => isPositiveInt(sponsor?.sponsorId))?.sponsorId;
  return isPositiveInt(sponsorId) ? sponsorId : undefined;
};

/** Service receipts: owner Sponsor = sponsor of the service protocol, protocolId = protocol of the service. */
export const getServiceStorageOwner = (
  protocolId: number | null | undefined,
  sponsorId: number | null | undefined
): AzureFileOwner | undefined => getProtocolSponsorStorageOwner(protocolId, sponsorId);

/** Why the receipts of a service cannot be managed (i18n key), or undefined when they can. */
export type ServiceReceiptUnavailableReason =
  | 'serviceReceipts.unavailable.notSaved'
  | 'serviceReceipts.unavailable.noProtocol'
  | 'serviceReceipts.unavailable.loadingSponsor'
  | 'serviceReceipts.unavailable.noSponsor';

export interface ServiceReceiptTargetResult {
  target: AzureFileTarget | undefined;
  unavailableReasonKey: ServiceReceiptUnavailableReason | undefined;
}

/**
 * Upload target of the receipts of a service (contract §10.2): entityType Service, entityId 0,
 * entityKey = service GUID, owner Sponsor + protocolId. `sponsor` is the resolution of the protocol sponsor
 * (undefined while it is being resolved, null when it could not be resolved).
 */
export const buildServiceReceiptTarget = (
  service: { id?: string | null; protocolId?: number | null } | null | undefined,
  sponsor: { status: 'loading' | 'loaded' | 'failed'; sponsorId?: number } | undefined
): ServiceReceiptTargetResult => {
  if (!service?.id || !isGuid(service.id)) {
    return { target: undefined, unavailableReasonKey: 'serviceReceipts.unavailable.notSaved' };
  }
  if (!isPositiveInt(service.protocolId)) {
    return { target: undefined, unavailableReasonKey: 'serviceReceipts.unavailable.noProtocol' };
  }
  if (!sponsor || sponsor.status === 'loading') {
    return { target: undefined, unavailableReasonKey: 'serviceReceipts.unavailable.loadingSponsor' };
  }
  const owner = getServiceStorageOwner(service.protocolId, sponsor.sponsorId);
  if (!owner) return { target: undefined, unavailableReasonKey: 'serviceReceipts.unavailable.noSponsor' };
  return {
    target: { entityType: AzureFileEntityTypeEnum.Service, entityId: 0, entityKey: service.id.toLowerCase(), owner },
    unavailableReasonKey: undefined,
  };
};

// ---- Storage folders of an upload target (contract §2) and registration status ----

/** Delays (ms) at which the drawer re-reads the storage and the registered files after an upload. */
export const AZURE_UPLOAD_RECHECK_DELAYS_MS = [AZURE_REGISTRATION_RELOAD_DELAY_MS, 10000];

/**
 * Folders (relative to the tenant root) where Contracts puts the files of a target:
 * `{ownerFolder}/{FileTypeName}/entidad_{EntityType}_{EntityId}`, one per allowed file type.
 * e.g. proveedor_45/ProviderInvoice/entidad_Provider_45,
 *      sponsor_12/protocolo_301/SponsorPaymentProposal/entidad_Protocol_301,
 *      sponsor_12/protocolo_301/SettlementInvoice/entidad_Settlement_88.
 * Empty when the target has no (valid) owner.
 */
export const getTargetStorageFolders = (
  target: AzureFileTarget,
  fileTypes: AzureFileTypeEnum[]
): TargetStorageFolder[] => {
  const ownerFolder = getOwnerFolderPath(target.owner);
  const hasId = target.entityKey ? isGuid(target.entityKey) : isPositiveInt(target.entityId);
  if (!ownerFolder || !hasId) return [];
  const entityId = getTargetRouteId(target);
  return Array.from(new Set(fileTypes)).map((fileType) => ({
    fileType,
    path: `${ownerFolder}/${AzureFileTypeEnum[fileType]}/entidad_${target.entityType}_${entityId}`,
  }));
};

const normalizeBlobPath = (value: string | null | undefined): string => {
  let path = (value ?? '').trim().replace(/\\/g, '/').replace(/^\/+/, '');
  try {
    path = decodeURIComponent(path);
  } catch {
    // keep it as it is
  }
  return path.toLowerCase();
};

/** A registered blob path (`tenant/...`) belongs to the storage file (path relative to the tenant). */
export const isSameBlob = (registeredBlobPath: string | null | undefined, relativePath: string): boolean => {
  const registered = normalizeBlobPath(registeredBlobPath);
  const relative = normalizeBlobPath(relativePath);
  if (!registered || !relative) return false;
  return registered === relative || registered.endsWith(`/${relative}`);
};

/**
 * Storage files of the target (source of truth) with the registration status:
 *  - registered: an owner-service record points to the blob,
 *  - pending: no record yet (it arrives by event, may take seconds),
 *  - unknown: the registered files could not be read (or were not read yet).
 * Sorted by date desc.
 */
export const mergeStorageWithRegistered = (
  storageFiles: TargetStorageFile[],
  registered: Pick<RegisteredAzureFile, 'blobPath'>[] | undefined,
  allowedTypes: AllowedAzureFileType[]
): DrawerStorageFile[] =>
  storageFiles
    .map(({ fileType, file }): DrawerStorageFile => {
      const allowed = allowedTypes.find((type) => type.fileType === fileType);
      return {
        key: file.path,
        path: file.path,
        name: file.name,
        extension: file.extension || getExtension(file.name),
        size: file.size,
        lastModified: file.lastModified,
        fileType,
        typeLabelKey: allowed?.labelKey ?? AzureFileTypeEnum[fileType],
        status:
          registered === undefined
            ? 'unknown'
            : registered.some((item) => isSameBlob(item.blobPath, file.path))
            ? 'registered'
            : 'pending',
      };
    })
    .sort((a, b) => (Date.parse(b.lastModified) || 0) - (Date.parse(a.lastModified) || 0));

// ---- direct registration and links (contract §9.3 / §9.5) ----

/** Owner service that registers each file type (contract §3). */
export const getRegisterKind = (fileType: AzureFileTypeEnum): AzureFileListLoaderKind => {
  switch (fileType) {
    case AzureFileTypeEnum.SponsorPaymentProposal:
      return 'protocolSponsor';
    case AzureFileTypeEnum.SettlementInvoice:
      return 'settlement';
    case AzureFileTypeEnum.ServiceReceipt:
      return 'service';
    case AzureFileTypeEnum.PractitionerInvoice:
      return 'creditorInvoice';
    case AzureFileTypeEnum.PaymentReceipt:
      return 'paymentReceipt';
    default:
      return 'provider';
  }
};

/** Owners whose drawer can link existing files (provider files since spec 005: providers/{id}/files/link). */
export const canLinkFiles = (kind: AzureFileListLoaderKind): kind is AzureFileLinkKind =>
  kind === 'protocolSponsor' ||
  kind === 'settlement' ||
  kind === 'service' ||
  kind === 'paymentReceipt' ||
  kind === 'provider';

/**
 * MedicalProtocols file type sent when linking a file to a provider (spec 005), from the extension:
 * pdf → Invoice (1), xlsx / xls / csv / docx → ServicesPerformed (2); anything else → undefined (not sent).
 */
export const getProviderLinkFileType = (path: string | null | undefined): ProviderFileTypeEnum | undefined => {
  const extension = getExtension(getPathName(path ?? ''));
  // pdf primero: MP 029 infiere pdf → factura (1); xlsx / xls / csv / docx → servicios realizados (2).
  if (AZURE_ALLOWED_EXTENSIONS[AzureFileTypeEnum.ProviderInvoice].includes(extension)) return ProviderFileTypeEnum.Invoice;
  if (AZURE_ALLOWED_EXTENSIONS[AzureFileTypeEnum.ProviderServicesPerformed].includes(extension)) {
    return ProviderFileTypeEnum.ServicesPerformed;
  }
  return undefined;
};

/** Upload / link target of the provider files (owner Provider = Provider.id, entity Provider_{id}). */
export const buildProviderFileTarget = (providerId: number | null | undefined): AzureFileTarget | undefined => {
  const owner = getProviderStorageOwner(providerId);
  return owner && isPositiveInt(providerId)
    ? { entityType: AzureFileEntityTypeEnum.Provider, entityId: providerId, owner }
    : undefined;
};

/** Maps the view model returned by register / link to the drawer model. */
export const mapOwnerFile = (kind: AzureFileListLoaderKind, file: OwnerFileViewModel): RegisteredAzureFile => {
  switch (kind) {
    case 'protocolSponsor':
      return mapSponsorFile(file as SponsorFileViewModel);
    case 'settlement':
      return mapSettlementFile(file as SettlementFileViewModel);
    case 'service':
      return mapServiceFile(file as ServiceFileViewModel);
    case 'paymentReceipt':
      return mapPaymentReceiptFile(file as PaymentReceiptViewModel);
    case 'creditorInvoice':
      return mapCreditorInvoiceFile(file as CreditorInvoiceFileViewModel);
    default:
      return mapProviderFile(file as ProviderFileViewModel);
  }
};

/** Registered blob path (`{tenant}/...`) → path relative to the tenant (always drops the tenant segment). */
export const stripTenantSegment = (blobPath: string | null | undefined): string => {
  const segments = (blobPath ?? '').replace(/\\/g, '/').split('/').filter((segment) => segment !== '');
  return segments.slice(1).join('/');
};

/**
 * Drawer rows: storage files of the target folders (with registration status) plus the files LINKED
 * to the target, which live in other folders (origin = Linked). A linked row whose blob is also one of
 * the storage rows is not duplicated. Sorted by date desc.
 */
export const mergeDrawerRows = (
  storageFiles: TargetStorageFile[],
  registered: RegisteredAzureFile[] | undefined,
  allowedTypes: AllowedAzureFileType[]
): DrawerStorageFile[] => {
  const storageRows = mergeStorageWithRegistered(
    storageFiles,
    registered?.filter((file) => file.origin !== AzureFileOriginEnum.Linked),
    allowedTypes
  );
  const fallbackType = allowedTypes[0];
  const linkedRows = (registered ?? [])
    .filter((file) => file.origin === AzureFileOriginEnum.Linked)
    .filter((file) => !storageRows.some((row) => isSameBlob(file.blobPath, row.path)))
    .map((file): DrawerStorageFile => {
      const path = file.path || stripTenantSegment(file.blobPath);
      return {
        key: `linked:${file.id}`,
        path,
        name: getPathName(path) || file.fileName,
        extension: file.fileExtension || getExtension(file.fileName),
        size: file.fileSize ?? -1,
        lastModified: file.uploadedAt,
        fileType: fallbackType?.fileType ?? AzureFileTypeEnum.SettlementInvoice,
        // The owner record knows its type (provider: invoice / services performed); else the drawer type.
        typeLabelKey: file.typeLabelKey || fallbackType?.labelKey || '',
        status: 'linked',
        registeredId: file.id,
        location: getParentPath(path),
      };
    });
  return [...storageRows, ...linkedRows].sort(
    (a, b) => (Date.parse(b.lastModified) || 0) - (Date.parse(a.lastModified) || 0)
  );
};
